// @ts-strict-ignore
import {
  Component,
  AfterViewInit,
  OnInit,
  ChangeDetectionStrategy,
  Renderer2, ViewChild, ViewContainerRef
} from '@angular/core';
import {
  SohoModalDialogRef,
  SohoModalDialogService,
  SohoModalDialogVetoableEventGuard
} from 'ids-enterprise-ng';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMGlobalPersonalization } from '@states/models/lm-global-personalization.model';
import { LMContextService } from '@services/lm-context.service';
import { environment } from '@environments/environment';
import { isEmpty } from 'lodash-es';

import { LMColorId, LMThemeService, LMThemeId } from '@banner/lm-theme.service';
import { LMDialogButtonKey, LMUniqueIdService, LMBrowserUtilService, AppConstants } from '@utils/index';
import { LMKeyboardShortcutsDialogComponent } from '@modal/lm-keyboard-shortcuts-dialog.component';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';
import { LMRoamingUIProfileService, RoamingUIProfileResult, RoamingUIProfileResponse } from '@services/lm-roamingUIProfile.service';
import { LMGlobalStateService } from '@app/states';
import { LMFormatUtilService } from "@app/services";
import { Subject } from "rxjs/internal/Subject";
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'lm-application-menu-settings-dialog',
  templateUrl: 'lm-application-menu-settings-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [ '.lm-inner-space { padding-left: 10px !important; padding-right: 10px !important; }' ],
  providers: [ LMRoamingUIProfileService ],
})
export class LMApplicationMenuSettingsDialogComponent implements SohoModalDialogVetoableEventGuard<any>, OnInit, AfterViewInit {

  @ViewChild('keyboardDialogPlaceholder', { read: ViewContainerRef, static: true }) placeHolder: ViewContainerRef;

  public JSON = JSON;
  public allowClose = false;
  public settings: LMGlobalPersonalization = {};
  public isCancelled: boolean;
  public availableApps: Array<LMWebAppSpec>;
  public isProduction = environment.production;
  public isDeveloper = this.lmContextService.isDeveloper();
  public availableThemes: SohoTheme[];
  public themeColors: SohoPersonalizationColors;
  private dialog: SohoModalDialogRef<any>;
  public usingPortalColor: boolean;
  public displayShortcuts: boolean;
  public selectedPreferredApp: string;
  public displayPreferredApplication: boolean;
  public usingPortalTheme: boolean
  public savedCompactFormLayout: boolean;
  public savedAppDisplay: string;
  public savedAccessibilityMode: boolean;
  public savedAutoFilter: string;
  public savedApplyColorToMenu: boolean;
  public savedToastMessagePosition: string;
  public toastMessageHasCustomPosition: boolean;
  public showDeletedRecords?: boolean;
  public showDeletedRecordEnabled?: boolean;
  public showArchive?: boolean;
  public showArchiveEnabled?: boolean;
  public themeSelectAttributes: SohoAttributesArray;
  public colorSelectAttributes: SohoAttributesArray;
  public searchModeSelectAttributes: SohoAttributesArray;
  public toastMessagePositionSelectAttributes: SohoAttributesArray;
  public toastMessageDurationSelectAttributes: SohoAttributesArray;
  public maxWaitTimePDFSelectAttributes: SohoAttributesArray;
  public exportRecordLimitDefaultAttributes: SohoAttributesArray;
  public preferredAppAttributes: SohoAttributesArray;

  public pickerOptions: SohoDatePickerOptions = {
    mode: 'standard',
    dateFormat: Soho.Locale.calendar().dateFormat.short,
    showMonthYearPicker: true
  };
  asOfDateChanged$ = new Subject<string>();

  public currentDate = new Date();
  public showAsOfDateError = false;
  public displayAllowSetAsOfDate = false;
  // private globalState: LMGlobalState = {};

  constructor(
    private renderer: Renderer2,
    private logger: LMLoggerService,
    private lmContextService: LMContextService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmThemeService: LMThemeService,
    private sohoModalDialogService: SohoModalDialogService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmBrowserUtilService: LMBrowserUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    protected lmLoggerService: LMLoggerService,
    private lmRoamingUIProfileService: LMRoamingUIProfileService,
    private lmGlobalStateService: LMGlobalStateService,
    private lmFormatUtilService: LMFormatUtilService
  ) {
    this.displayShortcuts = this.displayKeyboardShortcuts;
    this.displayPreferredApplication = this.displayPreferredApplicationSwitcher;
    this.displayAllowSetAsOfDate = this.allowSetAsOfDate;
  }

  ngOnInit() {
    this.availableThemes = this.lmThemeService.getAvailableThemes();
    this.themeColors = this.lmThemeService.getThemeColors();
    this.removeAlabasterFromDarkTheme();
    this.setIsUsingPortalThemeAndColor();

    this.themeSelectAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-theme' } ];
    this.colorSelectAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-color' } ];
    this.searchModeSelectAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-filter-mode' } ];
    this.toastMessagePositionSelectAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-toastMessagePosition' } ];
    this.toastMessageDurationSelectAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-toastMessageDuration' } ];
    this.maxWaitTimePDFSelectAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-max-wait-time' } ];
    this.exportRecordLimitDefaultAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-export-record-limit-default' } ];
    this.preferredAppAttributes = [ { name: 'data-automation-id', value: 'lm-settings-dialog-preferred-application' } ];

    const globalPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    if (!isEmpty(globalPersonalization)) {
      this.selectedPreferredApp = globalPersonalization.preferredWebApp;
      this.settings = globalPersonalization;
      if (globalPersonalization.useCompactFormLayout === undefined) {
        this.settings.useCompactFormLayout = this.lmContextService.getCompactnessMode() === 'compact';
      } else {
        this.savedCompactFormLayout = globalPersonalization.useCompactFormLayout;
      }
      this.savedApplyColorToMenu = globalPersonalization.applyColorToMenu;
      this.savedAppDisplay = globalPersonalization.appDisplay;
      this.savedAccessibilityMode = globalPersonalization.accessibilityMode;

      this.showDeletedRecords = this.lmContextService.getShowDeletedRecords();
      this.showDeletedRecordEnabled = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_ShowDeletedRecords);

      // check to see if the toast message was dragged.
      const toastMessageKey = (this.lmContextService.getActorUniqueId() === null) ? 'message' : this.lmContextService.getActorUniqueId();
      this.toastMessageHasCustomPosition = (localStorage.getItem('toast-' + toastMessageKey + '-usersettings-position')) ? true : false;
      if (this.toastMessageHasCustomPosition) {
        this.savedToastMessagePosition = 'BLANK';
        this.settings.toastMessagePosition = 'BLANK';
      } else {
        this.savedToastMessagePosition = (this.settings.toastMessagePosition) ? this.settings.toastMessagePosition : '';
        if (this.savedToastMessagePosition === '') {
          this.settings.toastMessagePosition = 'DEFAULT';
        }
      }

      this.showArchiveEnabled = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_ShowArchive);
      this.settings.archiveMode = this.showArchiveEnabled ? this.lmContextService.isArchiveMode() : false;

      const asOfDateSaved = this.lmContextService.getAsOfDate();
      this.settings.asOfDate = (asOfDateSaved) ? this.lmFormatUtilService.yyyymmddToDisplay(asOfDateSaved) : '';
      this.showAsOfDateError = false;

      if (globalPersonalization?.automaticSearch === undefined) {
        this.settings.automaticSearch = !this.lmContextService?.isFilterBarOnPowerMode() ? 'active' : 'passive';
      } else {
        this.savedAutoFilter = globalPersonalization.automaticSearch;
      }

      this.settings.maxWaitPDFCSV = (globalPersonalization?.maxWaitPDFCSV === undefined) ? 10 : globalPersonalization.maxWaitPDFCSV;
      this.settings.exportDefaultRecordLimit = globalPersonalization.exportDefaultRecordLimit === 'no-limit' ? 'no-limit' : 'system';

      if (!globalPersonalization.theme) {
        this.settings.theme = LMThemeService.DEFAULT_THEME;
      }

      if (!globalPersonalization.appDisplay && !globalPersonalization.color && !globalPersonalization.theme) {
        this.initializeAppMenuSettings();
      }
    } else {
      this.initializeAppMenuSettings();
    }
    this.availableApps = this.lmContextService.getAccessibleWebApps();
        // Sort by webApp name.
    this.availableApps.sort((a, b) => {
      if (a.webAppName < b.webAppName) {
        return -1;
      } else if (a.webAppName > b.webAppName) {
        return 1;
      } else {
        return 0;
      }
    });

        // remove any webApp without a title defined.
    for (let i = 0; i < this.availableApps.length; i++) {
      if (!this.availableApps[i].webAppTitle) {
        this.availableApps.splice(i, 1);
        i--;
      }
    }

    this.asOfDateChanged$.pipe( debounceTime(450)).subscribe(() => {
      this.validateAsOfDate();
    });

    this.registerCustomRules();
  }

  ngAfterViewInit() {
        // Add a class to the overlay to override the opacity
    this.renderer.addClass(document.body, 'lm-overlay');

        // this.hasProxy = this.proxyUserUrls != undefined || this.proxyResetUrls!== undefined || this.proxyManagementLink !== undefined;
        // this.hasAsOfDate = this.asOfDate !== undefined;
        // this.hasUserContext = this.userContextFields !== undefined;
        // this.changeDetectorRef.detectChanges();
  }

  registerCustomRules(){
    const invalidDate: any = {
      check: (value: any, field: any, grid: any) => {
        return this.validateAsOfDate();
      },
      id: 'custom',
      type: 'error',
      message: Soho.Locale.translate('AsOfDateValidationMessage')
    };

    Soho.Validation.rules['invalidDate'] = invalidDate;
  }

  validateAsOfDate(){
    if (!this.settings.asOfDate) {
      return true;
    }

    return (this.settings.asOfDate !== this.currentDate.toLocaleDateString());
  }

  initializeAppMenuSettings() {
    this.settings.activeColorPalette = { uplift: true, soho: false };
    this.settings.theme = LMThemeService.DEFAULT_THEME;
    this.settings.useCompactFormLayout = this.lmContextService.getCompactnessMode() === 'compact';
    this.settings.color = LMColorId.default;
    this.settings.appDisplay = 'webapp';
    this.settings.preferredWebApp = '';
    this.settings.accessibilityMode = true;
    this.settings.automaticSearch = !this.lmContextService?.isFilterBarOnPowerMode() ? 'active' : 'passive';
    this.settings.applyColorToMenu = true;
    this.settings.maxWaitPDFCSV = 10;
  }

  beforeClose(): boolean {
    this.allowClose = true;
    this.logger.trace([ '@LMApplicationMenuSettingsDialogComponent settings are ', this.settings ]);

    if (!this.isCancelled) {
      if (this.settings.theme === LMThemeService.DEFAULT_THEME) {
        delete this.settings.theme;
      }

      if (this.settings.toastMessagePosition) {
        if (this.settings.toastMessagePosition === 'DEFAULT'
                    || (this.settings.toastMessagePosition !== this.savedToastMessagePosition)) {
          const toastMessageKey = (this.lmContextService.getActorUniqueId() === null) ? 'message' : this.lmContextService.getActorUniqueId();
          localStorage.removeItem('toast-' + toastMessageKey + '-usersettings-position');
        }
      }

      if (this.settings.toastMessageDuration && this.settings.toastMessageDuration === 'BLANK') {
        delete this.settings.toastMessageDuration;
      }

      // eslint-disable-next-line eqeqeq
      if (this.settings.maxWaitPDFCSV && this.settings.maxWaitPDFCSV == 10) {
        delete this.settings.maxWaitPDFCSV;
      }

      if (this.settings.exportDefaultRecordLimit && this.settings.exportDefaultRecordLimit !== 'no-limit') {
        delete this.settings.exportDefaultRecordLimit;
      }

      // save the current value of useCompactFormLayout on the dialog box to compare it to the saved settings
      // to determine if a screen reload is needed.
      const showDeletedRecord = this.lmContextService.getShowDeletedRecords();
      const showArchive = Boolean(this.settings.archiveMode);
      const currentCompactFormLayout = this.settings.useCompactFormLayout;
      const currentAppDisplay = this.settings.appDisplay;
      const currentAccessibilityMode = this.settings.accessibilityMode;
      const currentAutoFilter = this.settings.automaticSearch;
      const currentApplyColorToMenu = this.settings.applyColorToMenu;
      const currentAsOfDate = this.settings.asOfDate;

      const globalState = this.lmGlobalStateService.getGlobalState();

      const needsReload = this.showDeletedRecords !== showDeletedRecord
                || globalState.archiveMode !== showArchive
                || this.savedCompactFormLayout !== currentCompactFormLayout
                || this.savedAppDisplay !== currentAppDisplay
                || this.savedAccessibilityMode !== currentAccessibilityMode
                || this.savedAutoFilter !== currentAutoFilter
                || this.savedApplyColorToMenu !== currentApplyColorToMenu
                || globalState.asOfDate !== currentAsOfDate;

      if (this.settings.useCompactFormLayout === (this.lmContextService.getCompactnessMode() === 'compact')) {
        delete this.settings.useCompactFormLayout;
      }

      // need to update the global state so the browser refresh can restart
      // in the correct mode!
      globalState.showdeletedrecords = this.showDeletedRecords;
      this.lmContextService.setShowDeletedRecords(this.showDeletedRecords);

      globalState.archiveMode = showArchive;
      this.lmContextService.setArchiveMode(showArchive);

      this.lmGlobalStateService.saveGlobalState(globalState);

      if (this.settings.preferredWebApp !== this.selectedPreferredApp) {
        this.lmRoamingUIProfileService.setPreferredWebApp(this.settings.preferredWebApp)
          .subscribe(
            (results: RoamingUIProfileResponse) => {
              if (results.status === RoamingUIProfileResult.COMPLETED) {
                this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.settings);
                if (needsReload) {
                  const currentRoute = document.location.href;
                  this.lmGlobalUtilService.navigate(currentRoute, null, 'replace');
                }
              }
            });
      } else {
        this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.settings);
                // Reload the current route with the new setting enabled.
                // Need to reload to clear CSS cache and see theme changes applied properly
        if (this.settings.asOfDate !== this.currentDate.toLocaleDateString()) {
          const dbDate = (this.settings.asOfDate) ? this.lmFormatUtilService.yyyymmddToData(this.settings.asOfDate) : '';
          const today = new Date();

          this.lmContextService.setAsOfDate(dbDate);

          if (dbDate) {
            if (Soho.Locale.parseDate(dbDate) < today) {
              this.lmContextService.setAsOfDateInPast(true);
            } else {
              this.lmContextService.setAsOfDateInPast(false);
            }
          }

          globalState.asOfDate = dbDate;
          this.lmGlobalStateService.saveGlobalState(globalState);
        }
        if (needsReload) {
          const currentRoute = document.location.href;
                    // window.location.replace(currentRoute);
          this.lmGlobalUtilService.navigate(currentRoute, null, 'replace');
        }
      }
    }

    if (this.isCancelled) {
      this.resetThemeOnCancel();
    }

    return this.allowClose;
  }

  theme(event) {
    const themeId: LMThemeId = event.currentTarget.value;
    this.lmThemeService.setTheme(themeId, this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout);

    this.themeColors = this.lmThemeService.getThemeColors();

        // save to settings
    this.settings.theme = this.lmThemeService.getCurrentThemeId();
    this.settings.color = this.lmThemeService.getCurrentColorId();

    if (this.lmThemeService.isUpliftTheme(themeId)) {
            // Safety
      if (this.settings.activeColorPalette) {
        this.settings.activeColorPalette.soho = false;
        this.settings.activeColorPalette.uplift = true;
      }
    } else {
            // Safety
      if (this.settings.activeColorPalette) {
        this.settings.activeColorPalette.soho = true;
        this.settings.activeColorPalette.uplift = false;
      }
    }

    this.removeAlabasterFromDarkTheme();
  }

  color(event) {
    const colorId: LMColorId = event.currentTarget.value;
    this.lmThemeService.setColor(colorId);
    this.settings.color = colorId;
  }

  setFilterBarMode(event) {
    const value = event?.currentTarget?.value;
    this.settings.automaticSearch = value;
  }

  appDisplay(event) {
    const value = event.currentTarget.value;
    this.settings.appDisplay = value;
  }

  toastMessagePosition(event) {
    const value = event.currentTarget.value;
    this.settings.toastMessagePosition = value;
  }

  toastMessageDuration(event) {
    const value = event.currentTarget.value;
    this.settings.toastMessageDuration = value;
  }

  setMaxWaitPDFCSV(event) {
    const value = event.currentTarget.value;
    this.settings.maxWaitPDFCSV = value;
  }

  preferredApp(event) {
    const value = event.currentTarget.value;
    this.settings.preferredWebApp = value;
  }

  changeAsOfDate(event) {
    this.settings.asOfDate = event.currentTarget.value;
    this.asOfDateChanged$.next(this.settings.asOfDate);
  }

  private resetThemeOnCancel() {
    const globalPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    const context = this.lmContextService.getContext();
    if (!isEmpty(globalPersonalization)) {
      this.settings.activeColorPalette = globalPersonalization.activeColorPalette;
      this.settings.theme = globalPersonalization.theme;
      this.settings.color = globalPersonalization.color;
    } else {
      this.settings.activeColorPalette = { uplift: true, soho: false };
      delete this.settings.theme;
      this.settings.color = LMColorId.default;
    }
    if (this.usingPortalTheme) {
      this.settings.theme = context.theme as LMThemeId;
    }
    if (this.usingPortalColor) {
      this.settings.color = context.color as LMColorId;
    }

    this.lmThemeService.setThemeAndColor(this.settings.theme, this.settings.color, this.settings.useCompactFormLayout);
  }

  private get displayKeyboardShortcuts(): boolean {
    if (this.lmBrowserUtilService.isMobile) {
      return false;
    }
    return true;
  }

  private get displayPreferredApplicationSwitcher(): boolean {
    this.availableApps = this.lmContextService.getAccessibleWebApps();

    try {
      const context = this.lmContextService.getContext();
      const configuredFeatures = context.configuredFeatures;
      const changePreferredApplicationStartPage = configuredFeatures.indexOf(AppConstants.FEATURE_ChangePreferredApplicationStartPage) > -1;

      if (this.availableApps.length === 1 || !changePreferredApplicationStartPage) {
        return false;
      }
      return true;
    } catch (e) {
      this.lmLoggerService.log([ 'LMApplicationMenuSettingsDialog', 'displayPreferredApplicationSwitcher', e ]);
      return false;
    }
  }

  private get allowSetAsOfDate(): boolean {
    if (!this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_AsOfDate)) {
      return false;
    }
    return true;
  }

  public openKeyboardShortcutsDialog() {
    const dialogId = 'KeyboardShortcuts';
    const buttons = [
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
        text: Soho.Locale.translate(LMDialogButtonKey.OK),
        click: (e, modal) => {
          modal.isCancelled = true;
          modal.close(true);
        },
        isDefault: true
      }
    ];

    this.dialog = this.sohoModalDialogService
      .modal(LMKeyboardShortcutsDialogComponent, this.placeHolder, { fullsize: 'responsive' })
      .buttons(buttons)
      .title(Soho.Locale.translate('KeyboardShortcuts'))
      .open();
  }

  private setIsUsingPortalThemeAndColor() {
    const context = this.lmContextService.getContext();
    if (this.lmContextService.getInforOSPortalVersion() !== '') {
      this.usingPortalColor = true;
      this.usingPortalTheme = true;
    } else {
      this.usingPortalColor = false; // Will always be false if not in Portal
      this.usingPortalTheme = (context.theme && context.theme !== 'Light');
    }
  }

  private removeAlabasterFromDarkTheme(): void {
    // Remove alabaster when dark
    // alabaster has issues with white text on white background in dark theme
    const themeName = this.lmThemeService.getCurrentThemeId();
    if (themeName?.includes('dark')) {
      delete this.themeColors['alabaster'];
    }
  }
}
